// app/javascript/controllers/utm_builder_controller.js
import { Controller } from "@hotwired/stimulus"
import { icone } from "../lib/icones"

export default class extends Controller {
  static targets = [
    "baseUrl",
    "source",
    "medium",
    "campaign",
    "content",
    "term",           // ✅ NEW: Missing target
    "generatedUrl",
    "copyButton"
  ]
  
  static values = {
    defaultBaseUrl: String  // ✅ NEW: Configurable default
  }

  connect() {
    console.log("🔗 UTM Builder connected")
    
    // ✅ FIX #1: Reset to defaults on connect (Turbo-safe)
    this.resetIfEmpty()
    
    // ✅ Always update URL on connect
    this.updateUrl()
  }
  
  // ✅ NEW: Reset form if coming from Turbo cache
  resetIfEmpty() {
    if (!this.baseUrlTarget.value && this.hasDefaultBaseUrlValue) {
      this.baseUrlTarget.value = this.defaultBaseUrlValue
    }
  }

  updateUrl() {
    const baseUrl = this.baseUrlTarget.value.trim()
    
    // ✅ FIX #2: Validate base URL
    if (!this.isValidUrl(baseUrl)) {
      this.generatedUrlTarget.value = ''
      console.warn('Invalid base URL:', baseUrl)
      return
    }
    
    const params = []

    // ✅ FIX #3: Sanitize input values
    if (this.sourceTarget.value) {
      params.push(`utm_source=${this.sanitizeParam(this.sourceTarget.value)}`)
    }
    if (this.mediumTarget.value) {
      params.push(`utm_medium=${this.sanitizeParam(this.mediumTarget.value)}`)
    }
    if (this.campaignTarget.value) {
      params.push(`utm_campaign=${this.sanitizeParam(this.campaignTarget.value)}`)
    }
    if (this.hasContentTarget && this.contentTarget.value) {
      params.push(`utm_content=${this.sanitizeParam(this.contentTarget.value)}`)
    }
    if (this.hasTermTarget && this.termTarget.value) {
      params.push(`utm_term=${this.sanitizeParam(this.termTarget.value)}`)
    }

    const generatedUrl = params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl
    this.generatedUrlTarget.value = generatedUrl
    
    console.log('Generated URL:', generatedUrl)
  }
  
  // ✅ NEW: Validate URL format
  isValidUrl(url) {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }
  
  // ✅ NEW: Sanitize UTM parameters
  sanitizeParam(value) {
    return encodeURIComponent(
      value.trim()
        .toLowerCase()
        .replace(/\s+/g, '_')      // Spaces to underscores
        .replace(/[^a-z0-9_-]/g, '') // Remove special chars
    )
  }

  copy(event) {
    event?.preventDefault()
    
    const url = this.generatedUrlTarget.value
    
    if (!url) {
      console.warn('Nothing to copy')
      return
    }
    
    // Modern clipboard API
    navigator.clipboard.writeText(url)
      .then(() => {
        this.showCopyFeedback()
        console.log('✓ URL copied:', url)
      })
      .catch(err => {
        console.error('Copy failed:', err)
        // Fallback: select text
        this.generatedUrlTarget.select()
      })
  }

  showCopyFeedback() {
    const button = this.copyButtonTarget
    const originalHTML = button.innerHTML
    
    // Update button
    button.innerHTML = `${icone("check")} Copied!`
    button.classList.add('bg-beb-success', 'hover:bg-beb-success/90')
    button.classList.remove('bg-beb-brand', 'hover:bg-beb-brand-hover')
    button.disabled = true
    
    // ✅ FIX #4: Store timeout ID for cleanup
    this.copyTimeout = setTimeout(() => {
      button.innerHTML = originalHTML
      button.classList.remove('bg-beb-success', 'hover:bg-beb-success/90')
      button.classList.add('bg-beb-brand', 'hover:bg-beb-brand-hover')
      button.disabled = false
      this.copyTimeout = null
    }, 2000)
  }

  applyTemplate(event) {
    event?.preventDefault()
    
    const template = event.currentTarget.dataset.template
    
    const templates = {
      facebook: { 
        source: 'facebook_ad', 
        medium: 'cpc', 
        campaign: this.generateCampaignName() 
      },
      instagram: { 
        source: 'instagram_story', 
        medium: 'social', 
        campaign: this.generateCampaignName() 
      },
      google: { 
        source: 'google_ads', 
        medium: 'cpc', 
        campaign: this.generateCampaignName() 
      },
      email: { 
        source: 'email_newsletter', 
        medium: 'email', 
        campaign: this.generateCampaignName() 
      },
      linkedin: { 
        source: 'linkedin_ad', 
        medium: 'cpc', 
        campaign: this.generateCampaignName() 
      },
      twitter: { 
        source: 'twitter_ad', 
        medium: 'social', 
        campaign: this.generateCampaignName() 
      }
    }

    const data = templates[template]
    if (data) {
      this.sourceTarget.value = data.source
      this.mediumTarget.value = data.medium
      this.campaignTarget.value = data.campaign
      this.updateUrl()
      console.log('Applied template:', template)
    } else {
      console.warn('Unknown template:', template)
    }
  }
  
  // ✅ NEW: Generate timestamp-based campaign name
  generateCampaignName() {
    const now = new Date()
    const month = now.toLocaleString('en', { month: 'short' }).toLowerCase()
    const year = now.getFullYear()
    return `${month}_${year}`
  }
  
  // ✅ NEW: Reset all fields
  reset(event) {
    event?.preventDefault()
    
    this.sourceTarget.value = ''
    this.mediumTarget.value = ''
    this.campaignTarget.value = ''
    if (this.hasContentTarget) this.contentTarget.value = ''
    if (this.hasTermTarget) this.termTarget.value = ''
    
    this.updateUrl()
    console.log('UTM form reset')
  }

  // ✅ FIX #5: Cleanup timeout on disconnect
  disconnect() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout)
      this.copyTimeout = null
    }
    console.log("🔗 UTM Builder disconnected")
  }
}
